import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Briefcase, Clock, Users } from 'lucide-react';

const openPositions = [
  {
    id: 1,
    title: 'Event Manager',
    department: 'Operations',
    location: 'Hyderabad',
    type: 'Full-time',
    experience: '3-5 years',
    description: 'Lead and manage end-to-end event execution for corporate clients and exhibitions.'
  },
  {
    id: 2,
    title: 'Business Development Executive',
    department: 'Sales',
    location: 'Hyderabad',
    type: 'Full-time',
    experience: '2-4 years',
    description: 'Drive new business acquisition and maintain client relationships in the events industry.'
  },
  {
    id: 3,
    title: 'Creative Designer',
    department: 'Design',
    location: 'Hyderabad / Remote',
    type: 'Full-time',
    experience: '2-3 years',
    description: 'Create compelling visual designs for exhibition booths, event collaterals, and marketing materials.'
  },
  {
    id: 4,
    title: 'Operations Coordinator',
    department: 'Operations',
    location: 'Hyderabad',
    type: 'Full-time',
    experience: '1-2 years',
    description: 'Coordinate logistics, vendor management, and on-ground operations for events.'
  }
];

const benefits = [
  'Competitive salary packages',
  'Health insurance coverage',
  'Learning & development opportunities',
  'Dynamic work environment',
  'Career growth potential',
  'Work on exciting projects'
];

export default function CareersPage() {
  return (
    <div className="page-transition" data-testid="careers-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-navy overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Join Our Team
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                Build Your Career with <span className="text-gold">DFAIRS</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                Join a team of passionate professionals who are shaping the future of events and exhibitions in India.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Why Join Us */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                  Why DFAIRS?
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mb-6">
                  A Great Place to Grow
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                  At DFAIRS, we believe in nurturing talent and providing opportunities for professional growth. Join us in creating extraordinary event experiences while building a rewarding career.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {benefits.map((benefit) => (
                    <div key={benefit} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-gold rounded-full" />
                      <span className="text-navy">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
                  alt="Team collaboration"
                  className="w-full h-[500px] object-cover"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-20 lg:py-32 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Current Openings
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-navy mb-6">
                Open Positions
              </h2>
              <p className="text-muted-foreground text-lg">
                Explore our current job openings and find the perfect role for your career.
              </p>
            </motion.div>

            <div className="space-y-6">
              {openPositions.map((position, index) => (
                <motion.div
                  key={position.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 hover-lift"
                  data-testid={`job-${position.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-3 mb-3">
                        <span className="bg-gold/10 text-gold px-3 py-1 text-sm font-medium">
                          {position.department}
                        </span>
                        <span className="bg-navy/10 text-navy px-3 py-1 text-sm font-medium">
                          {position.type}
                        </span>
                      </div>
                      <h3 className="font-heading text-xl lg:text-2xl text-navy mb-2">
                        {position.title}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {position.description}
                      </p>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4 text-gold" />
                          {position.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4 text-gold" />
                          {position.experience}
                        </span>
                      </div>
                    </div>
                    <div>
                      <Link
                        to="/contact"
                        className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
                      >
                        Apply Now
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-24 bg-navy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl md:text-4xl text-white mb-6"
            >
              Don't See a Role That Fits?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-white/80 text-lg mb-10 max-w-2xl mx-auto"
            >
              We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Send Your Resume
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
